import { useState } from "react";
import { Upload, X, Plus, Loader2, Trash2 } from "lucide-react";
import api, { assetUrl } from "@/lib/api";
import { toast } from "sonner";

export const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    {label && <label className="block text-sm font-semibold text-slate-700">{label}</label>}
    {children}
    {hint && <p className="text-xs text-slate-400">{hint}</p>}
  </div>
);

export const TextInput = ({ value, onChange, placeholder, testid }) => (
  <input
    type="text"
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testid}
    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition"
  />
);

export const TextArea = ({ value, onChange, rows = 4, placeholder, testid }) => (
  <textarea
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    placeholder={placeholder}
    data-testid={testid}
    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition resize-y"
  />
);

export const ImageUpload = ({ value, onChange, testid }) => {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Gambar berhasil diunggah");
    } catch (err) {
      toast.error("Gagal mengunggah gambar");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="w-24 h-24 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center flex-shrink-0">
        {value ? (
          <img src={assetUrl(value)} alt="preview" className="w-full h-full object-cover" />
        ) : (
          <span className="text-xs text-slate-400">Tidak ada</span>
        )}
      </div>
      <div className="flex-1 space-y-2">
        <label
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold cursor-pointer transition"
          data-testid={testid}
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Mengunggah..." : "Unggah Gambar"}
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
        </label>
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="atau tempel URL gambar"
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs text-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-400"
        />
      </div>
    </div>
  );
};

export const StringList = ({ items, onChange, placeholder = "Tambah item", testidPrefix }) => {
  const update = (i, v) => {
    const next = [...items];
    next[i] = v;
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);

  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="text"
            value={it}
            onChange={(e) => update(i, e.target.value)}
            data-testid={testidPrefix ? `${testidPrefix}-${i}` : undefined}
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
          />
          <button
            onClick={() => remove(i)}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition flex-shrink-0"
            aria-label="Hapus"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        onClick={add}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-sky-300 text-sky-600 text-sm font-semibold hover:bg-sky-50 transition"
      >
        <Plus className="w-4 h-4" />
        {placeholder}
      </button>
    </div>
  );
};

export const Card = ({ children, onRemove, title }) => (
  <div className="relative p-5 rounded-2xl border border-slate-200 bg-slate-50/60 space-y-3">
    {onRemove && (
      <button
        onClick={onRemove}
        className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-100 transition"
        aria-label="Hapus"
      >
        <X className="w-4 h-4" />
      </button>
    )}
    {title && <div className="text-xs font-bold uppercase tracking-wide text-slate-400">{title}</div>}
    {children}
  </div>
);
